import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import { SEO_META, CUSTOMIZATION_OPTIONS, IMAGES } from "../config/site";

const MODES = [
  { title: "OEM", text: "We manufacture to your designs, tech packs and specifications." },
  { title: "Private Label", text: "Your labels, tags and packaging on garments we develop and produce." },
  { title: "Custom Uniforms", text: "Programs developed from a brief, reference sample or existing uniform." },
];

export default function CustomManufacturing() {
  const meta = SEO_META.custom;
  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="border-b border-obsidian/10 bg-obsidian text-white" data-testid="custom-hero">
        <div className="container-site grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
          <Reveal>
            <p className="eyebrow !text-white/50">Custom Manufacturing</p>
            <h1 className="mt-4 font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">
              Your brand. Your requirements. Your uniform.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/60">
              Built for sourcing managers: send a tech pack, a reference sample or a written brief, and we
              develop the garment around your specification — fabric, fit, branding and finishing included.
            </p>
            <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="custom-hero-quote-btn" className="btn-primary mt-8">
              Start Your Specification <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.fabricMacro} alt="Close-up of precision stitching on technical fabric" className="aspect-[3/4] w-full object-cover" loading="lazy" />
              <img src={IMAGES.sewing} alt="Industrial sewing machine constructing a uniform garment" className="mt-8 aspect-[3/4] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 lg:py-24" data-testid="custom-options">
        <Reveal>
          <p className="eyebrow">What you can customize</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
            Every detail, decided by you
          </h2>
          <p className="mt-4 max-w-xl text-base text-obsidian/60">
            Options below are available depending on product specification — we confirm feasibility for each
            style during development. Shortlist materials first in our{" "}
            <Link to="/fabrics" className="font-semibold text-cleef underline underline-offset-4" data-testid="custom-fabrics-link">fabric library</Link>.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2 lg:grid-cols-3">
          {CUSTOMIZATION_OPTIONS.map((opt, i) => (
            <Reveal key={opt} delay={(i % 3) * 0.05} className="bg-ivory">
              <div className="flex items-center gap-3 p-5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-cleef/30 bg-cleef-light">
                  <Check className="h-3.5 w-3.5 text-cleef" />
                </span>
                <span className="text-sm font-medium text-obsidian/85">{opt}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-obsidian/10 bg-white" data-testid="custom-modes">
        <div className="container-site py-16 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <p className="eyebrow">How we work with brands</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
                OEM · Private Label · Custom Uniforms
              </h2>
              <div className="frame-offset mt-8 overflow-hidden">
                <img src={IMAGES.flatlay} alt="Neatly folded custom uniform garments ready for packing" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <div className="grid content-center gap-px border border-obsidian/10 bg-obsidian/10 lg:col-span-8">
              {MODES.map((m, i) => (
                <Reveal key={m.title} delay={i * 0.08} className="bg-white">
                  <div className="flex items-start gap-6 p-8">
                    <span className="font-mono text-2xl font-semibold text-cleef">0{i + 1}</span>
                    <div>
                      <h3 className="font-display text-xl font-bold tracking-tight">{m.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-obsidian/60">{m.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
      <CTABlock />
    </>
  );
}
